import React, { useEffect, useState } from 'react';
import { api, LEVEL_COLORS } from '../utils/api';
import AdminLogin, { checkAdminAuth } from './AdminLogin';

// ─── MODAL WRAPPER ──────────────────────────────────────────────────────────
function Modal({ title, onClose, children }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-title">{title}</div>
        {children}
      </div>
    </div>
  );
}

// ─── PRODUCT FORM ───────────────────────────────────────────────────────────
function ProductForm({ product, categories, allProducts, onSave, onClose }) {
  const existingImages = product?.images
    ? (typeof product.images === 'string' ? JSON.parse(product.images) : product.images)
    : product?.image ? [product.image] : [];

  // Initialize selected categories from extra_categories or category_id
  const initCatIds = () => {
    if (product?.extra_categories && Array.isArray(product.extra_categories) && product.extra_categories.length > 0) {
      return product.extra_categories.map(c => c.id);
    }
    return product?.category_id ? [product.category_id] : [];
  };

  const [form, setForm] = useState({
    name: product?.name || '',
    description: product?.description || '',
    level: product?.level || 'מתחיל',
    min_age: product?.min_age || 6,
    price: product?.price || '',
    sale_price: product?.sale_price || '',
    cost_price: product?.cost_price || '',
    stock_quantity: product?.stock_quantity ?? 0,
    product_code: product?.product_code || '',
    recommended_product_id: product?.recommended_product_id || '',
  });
  const [selectedCategories, setSelectedCategories] = useState(initCatIds);
  const [newFiles, setNewFiles] = useState([]);
  const [previews, setPreviews] = useState(existingImages);
  const [primaryIndex, setPrimaryIndex] = useState(0);
  const [saving, setSaving] = useState(false);

  const toggleCategory = (catId) => {
    setSelectedCategories(prev =>
      prev.includes(catId) ? prev.filter(id => id !== catId) : [...prev, catId]
    );
  };

  const handleFiles = (e) => {
    const files = Array.from(e.target.files);
    setNewFiles(files);
    setPreviews(files.map(f => URL.createObjectURL(f)));
    setPrimaryIndex(0);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.price) { alert('שם ומחיר חובה'); return; }
    setSaving(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append('category_ids', JSON.stringify(selectedCategories));

    if (newFiles.length > 0) {
      // New files uploaded — reorder so primary is first
      const ordered = [...newFiles];
      if (primaryIndex > 0 && ordered.length > 1) {
        const [primary] = ordered.splice(primaryIndex, 1);
        ordered.unshift(primary);
      }
      ordered.forEach(f => fd.append('images', f));
    } else if (primaryIndex > 0 && existingImages.length > 0) {
      // No new files — reorder existing images and send as existing_images_order
      const reordered = [...existingImages];
      const [primary] = reordered.splice(primaryIndex, 1);
      reordered.unshift(primary);
      fd.append('existing_images_order', JSON.stringify(reordered));
    }

    try {
      if (product) await api.putForm(`/products/${product.id}`, fd);
      else await api.postForm('/products', fd);
      onSave();
    } finally { setSaving(false); }
  };

  const set = f => e => setForm(prev => ({ ...prev, [f]: e.target.value }));

  const profit = (form.price && form.cost_price) ? parseFloat(form.price) - parseFloat(form.cost_price) : null;
  const maaser = profit ? profit * 0.1 : null;

  return (
    <>
      <div className="form-group">
        <label className="form-label">שם המוצר *</label>
        <input className="form-input" value={form.name} onChange={set('name')} />
      </div>
      <div className="form-group">
        <label className="form-label">תיאור</label>
        <textarea className="form-textarea" value={form.description} onChange={set('description')} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      <div className="form-group">
        <label className="form-label">קטגוריות (אפשר לבחור כמה)</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '8px 0' }}>
          {categories.map(c => (
            <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', background: selectedCategories.includes(c.id) ? 'var(--primary)' : 'var(--bg-soft)', color: selectedCategories.includes(c.id) ? 'white' : 'var(--text)', padding: '4px 12px', borderRadius: 20, fontSize: 13, border: '1px solid var(--border)', transition: 'all .15s' }}>
              <input type="checkbox" checked={selectedCategories.includes(c.id)} onChange={() => toggleCategory(c.id)} style={{ display: 'none' }} />
              {c.name}
            </label>
          ))}
        </div>
      </div>
        <div className="form-group">
          <label className="form-label">רמה</label>
          <select className="form-select" value={form.level} onChange={set('level')}>
            <option>מתחיל</option><option>מתקדם</option><option>מקצועי</option>
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">גיל מינימום</label>
          <input className="form-input" type="number" min={3} value={form.min_age} onChange={set('min_age')} />
        </div>
        <div className="form-group">
          <label className="form-label">מחיר מחירון (₪) *</label>
          <input className="form-input" type="number" value={form.price} onChange={set('price')} />
        </div>
        <div className="form-group">
          <label className="form-label">מחיר מבצע (₪)</label>
          <input className="form-input" type="number" value={form.sale_price} onChange={set('sale_price')} placeholder="ריק = ללא מבצע" />
        </div>
        <div className="form-group">
          <label className="form-label">🔒 מחיר מקורי (₪)</label>
          <input className="form-input" type="number" value={form.cost_price} onChange={set('cost_price')} placeholder="עלות המוצר" />
        </div>
      </div>

      {profit !== null && (
        <div style={{ background: 'var(--bg-soft)', borderRadius: 10, padding: '10px 14px', marginBottom: 12, display: 'flex', gap: 24, fontSize: 13 }}>
          <span>💰 <strong>רווח:</strong> ₪{profit.toFixed(2)}</span>
          <span>🕍 <strong>מעשר (10%):</strong> ₪{maaser.toFixed(2)}</span>
        </div>
      )}

      <div className="form-group">
        <label className="form-label">🔖 קוד מוצר (אופציונלי)</label>
        <input className="form-input" value={form.product_code} onChange={set('product_code')} placeholder="לדוגמה: ABC-001" dir="ltr" />
      </div>

      <div className="form-group">
        <label className="form-label">📦 כמות במלאי</label>
        <input className="form-input" type="number" min={0} value={form.stock_quantity}
          onChange={e => setForm(f => ({ ...f, stock_quantity: e.target.value }))} />
        {Number(form.stock_quantity) <= 0 && (
          <div style={{ fontSize: 12, color: '#E67E22', marginTop: 4 }}>⚠️ המוצר יוצג כ"אזל" באתר</div>
        )}
      </div>

      <div className="form-group">
        <label className="form-label">💡 מוצר מומלץ (יוצע ללקוח בעת הוספה לעגלה)</label>
        <select className="form-select" value={form.recommended_product_id} onChange={set('recommended_product_id')}>
          <option value="">-- ללא המלצה --</option>
          {allProducts
            .filter(p => !product || p.id !== product.id)
            .map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {/* תמונות */}
      <div className="form-group">
        <label className="form-label">
          תמונות (עד 6) {existingImages.length > 0 ? '— העלאה חדשה תחליף את הקיימות' : ''}
        </label>
        <input type="file" accept="image/*" multiple onChange={handleFiles} style={{ fontSize: 14 }} />

        {previews.length > 0 && (
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <div style={{ width: '100%', fontSize: 12, color: 'var(--text-light)', marginBottom: 4 }}>לחצי על תמונה כדי לסמן אותה כראשית</div>
            {previews.map((src, i) => (
              <div key={i} style={{ position: 'relative', cursor: 'pointer' }} onClick={() => setPrimaryIndex(i)}>
                <img src={src} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: i === primaryIndex ? '3px solid var(--primary)' : '2px solid var(--border)', opacity: i === primaryIndex ? 1 : 0.75, transition: 'all .2s' }} />
                {i === primaryIndex && <span style={{ position: 'absolute', bottom: 2, right: 2, background: 'var(--primary)', color: 'white', fontSize: 9, borderRadius: 4, padding: '1px 4px' }}>ראשית</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="modal-actions">
        <button className="btn btn-cancel" onClick={onClose}>ביטול</button>
        <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>{saving ? 'שומר...' : '💾 שמור'}</button>
      </div>
    </>
  );
}

// ─── PRODUCTS SECTION ───────────────────────────────────────────────────────
function ProductsSection() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [modal, setModal] = useState(null);
  const [detailModal, setDetailModal] = useState(null);

  const load = () => Promise.all([api.get('/products'), api.get('/categories')]).then(([p, c]) => { setProducts(p); setCategories(c); });
  useEffect(() => { load(); }, []);

  const deleteProduct = async (id) => {
    if (!window.confirm('למחוק מוצר זה?')) return;
    await api.delete(`/products/${id}`);
    load();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--brown-dark)' }}>🛍️ ניהול מוצרים</h2>
        <button className="btn btn-primary" onClick={() => setModal('add')}>+ הוסף מוצר</button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>תמונה</th><th>קוד</th><th>שם</th><th>קטגוריה</th><th>רמה</th><th>גיל</th>
              <th>מחיר מחירון</th><th>מבצע</th><th>מלאי</th><th>פעולות</th>
            </tr>
          </thead>
          <tbody>
            {products.map(p => (
              <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => setDetailModal(p)}>
                <td>
                  {p.image
                    ? <img src={p.image} alt="" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }} />
                    : <span style={{ fontSize: 28 }}>🎨</span>}
                </td>
                <td style={{ fontSize: 13, color: 'var(--text-light)', direction: 'ltr' }}>{p.product_code || '—'}</td>
                <td><strong>{p.name}</strong></td>
                <td>{p.extra_categories && p.extra_categories.length > 0 ? p.extra_categories.map(c => c.name).join(', ') : (p.category_name || '—')}</td>
                <td><span style={{ color: LEVEL_COLORS[p.level], fontWeight: 600 }}>{p.level}</span></td>
                <td>{p.min_age}+</td>
                <td>₪{p.price.toFixed(2)}</td>
                <td>{p.sale_price ? <span style={{ color: 'red', fontWeight: 700 }}>₪{p.sale_price.toFixed(2)}</span> : '—'}</td>
                <td>
                  {p.stock_quantity > 0
                    ? <span style={{ color: p.stock_quantity <= 5 ? '#E67E22' : 'green', fontWeight: 700 }}>{p.stock_quantity} יח'</span>
                    : <span style={{ color: '#888' }}>אזל</span>}
                </td>
                <td onClick={e => e.stopPropagation()}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-primary btn-sm" onClick={() => setModal(p)}>✏️</button>
                    <button className="btn btn-danger btn-sm" onClick={() => deleteProduct(p.id)}>🗑</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {detailModal && (
        <Modal title={`🔒 פרטים פיננסיים: ${detailModal.name}`} onClose={() => setDetailModal(null)}>
          {(() => {
            const profit = detailModal.cost_price ? (detailModal.price - detailModal.cost_price) : null;
            const maaser = profit !== null ? profit * 0.1 : null;
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--bg-soft)', borderRadius: 8 }}>
                  <span>מחיר מחירון</span><strong>₪{detailModal.price.toFixed(2)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', background: '#f5f5f5', borderRadius: 8 }}>
                  <span>🔒 מחיר מקורי</span><strong>{detailModal.cost_price ? `₪${detailModal.cost_price.toFixed(2)}` : '— לא הוגדר —'}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', background: '#f5f5f5', borderRadius: 8 }}>
                  <span>🔒 רווח</span>
                  <strong style={{ color: profit > 0 ? 'var(--green)' : 'var(--red)' }}>{profit !== null ? `₪${profit.toFixed(2)}` : '—'}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', background: '#f5f5f5', borderRadius: 8 }}>
                  <span>🔒 מעשר (10%)</span>
                  <strong style={{ color: 'var(--purple)' }}>{maaser !== null ? `₪${maaser.toFixed(2)}` : '—'}</strong>
                </div>
              </div>
            );
          })()}
          <div className="modal-actions">
            <button className="btn btn-cancel" onClick={() => setDetailModal(null)}>סגור</button>
            <button className="btn btn-primary" onClick={() => { setModal(detailModal); setDetailModal(null); }}>✏️ ערוך מוצר</button>
          </div>
        </Modal>
      )}
      {modal && (
        <Modal title={modal === 'add' ? '+ מוצר חדש' : `✏️ עריכה: ${modal.name}`} onClose={() => setModal(null)}>
          <ProductForm product={modal === 'add' ? null : modal} categories={categories} allProducts={products} onSave={() => { load(); setModal(null); }} onClose={() => setModal(null)} />
        </Modal>
      )}
    </div>
  );
}

// ─── CATEGORIES SECTION ─────────────────────────────────────────────────────
function CategoriesSection() {
  const [categories, setCategories] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', sort_order: 0 });

  const load = () => api.get('/categories').then(setCategories);
  useEffect(() => { load(); }, []);

  const openEdit = (cat) => { setForm({ name: cat.name, description: cat.description || '', sort_order: cat.sort_order }); setModal(cat); };
  const openAdd = () => { setForm({ name: '', description: '', sort_order: 0 }); setModal('add'); };

  const handleSave = async () => {
    if (!form.name) { alert('שם חובה'); return; }
    if (modal === 'add') await api.post('/categories', form);
    else await api.put(`/categories/${modal.id}`, form);
    load(); setModal(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('למחוק קטגוריה זו?')) return;
    const res = await api.delete(`/categories/${id}`);
    if (res.error) { alert(res.error); return; }
    load();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--brown-dark)' }}>📂 ניהול קטגוריות</h2>
        <button className="btn btn-primary" onClick={openAdd}>+ קטגוריה חדשה</button>
      </div>
      <table className="admin-table">
        <thead><tr><th>#</th><th>שם</th><th>תיאור</th><th>סדר</th><th>פעולות</th></tr></thead>
        <tbody>
          {categories.map(c => (
            <tr key={c.id}>
              <td>{c.id}</td>
              <td><strong>{c.name}</strong></td>
              <td>{c.description || '—'}</td>
              <td>{c.sort_order}</td>
              <td>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-primary btn-sm" onClick={() => openEdit(c)}>✏️</button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(c.id)}>🗑</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {modal && (
        <Modal title={modal === 'add' ? '+ קטגוריה חדשה' : `✏️ עריכה: ${modal.name}`} onClose={() => setModal(null)}>
          <div className="form-group"><label className="form-label">שם *</label><input className="form-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
          <div className="form-group"><label className="form-label">תיאור</label><input className="form-input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></div>
          <div className="form-group"><label className="form-label">סדר תצוגה</label><input className="form-input" type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: +e.target.value }))} /></div>
          <div className="modal-actions">
            <button className="btn btn-cancel" onClick={() => setModal(null)}>ביטול</button>
            <button className="btn btn-primary" onClick={handleSave}>💾 שמור</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── PICKUP POINTS SECTION ──────────────────────────────────────────────────
function PickupSection() {
  const [points, setPoints] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', city: '', notes: '', fee: '', active: true });

  const load = () => api.get('/pickup-points/all').then(setPoints);
  useEffect(() => { load(); }, []);

  const openEdit = (p) => { setForm({ name: p.name, address: p.address, city: p.city, notes: p.notes || '', fee: p.fee || '', active: !!p.active }); setModal(p); };
  const openAdd = () => { setForm({ name: '', address: '', city: '', notes: '', fee: '', active: true }); setModal('add'); };

  const handleSave = async () => {
    if (!form.name || !form.address || !form.city) { alert('שם, כתובת ועיר חובה'); return; }
    if (modal === 'add') await api.post('/pickup-points', form);
    else await api.put(`/pickup-points/${modal.id}`, form);
    load(); setModal(null);
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`למחוק את נקודת החלוקה "${p.name}"?`)) return;
    await api.delete(`/pickup-points/${p.id}`);
    load();
  };

  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--brown-dark)' }}>📍 נקודות חלוקה</h2>
        <button className="btn btn-primary" onClick={openAdd}>+ נקודה חדשה</button>
      </div>
      <table className="admin-table">
        <thead><tr><th>שם</th><th>כתובת</th><th>עיר</th><th>שעות</th><th>דמי משלוח</th><th>פעיל</th><th>פעולות</th></tr></thead>
        <tbody>
          {points.map(p => (
            <tr key={p.id}>
              <td><strong>{p.name}</strong></td>
              <td>{p.address}</td>
              <td>{p.city}</td>
              <td style={{ fontSize: 13, color: 'var(--text-light)' }}>{p.notes || '—'}</td>
              <td>{p.fee > 0 ? <span style={{ fontWeight: 700, color: 'var(--primary-dark)' }}>₪{parseFloat(p.fee).toFixed(2)}</span> : <span style={{ color: '#bbb' }}>חינם</span>}</td>
              <td>{p.active ? <span style={{ color: 'green' }}>✓</span> : <span style={{ color: '#bbb' }}>✗</span>}</td>
              <td>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-primary btn-sm" onClick={() => openEdit(p)}>✏️ ערוך</button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(p)}>🗑 מחק</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {modal && (
        <Modal title={modal === 'add' ? '+ נקודה חדשה' : `✏️ ${modal.name}`} onClose={() => setModal(null)}>
          <div className="form-group"><label className="form-label">שם *</label><input className="form-input" value={form.name} onChange={set('name')} /></div>
          <div className="form-group"><label className="form-label">כתובת *</label><input className="form-input" value={form.address} onChange={set('address')} /></div>
          <div className="form-group"><label className="form-label">עיר *</label><input className="form-input" value={form.city} onChange={set('city')} /></div>
          <div className="form-group"><label className="form-label">שעות פתיחה / הערות</label><input className="form-input" value={form.notes} onChange={set('notes')} /></div>
          <div className="form-group"><label className="form-label">💰 דמי משלוח (₪) — 0 = חינם</label><input className="form-input" type="number" min="0" value={form.fee} onChange={set('fee')} placeholder="0" /></div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input type="checkbox" id="active" checked={!!form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} />
            <label htmlFor="active" style={{ cursor: 'pointer', fontWeight: 600 }}>נקודה פעילה</label>
          </div>
          <div className="modal-actions">
            <button className="btn btn-cancel" onClick={() => setModal(null)}>ביטול</button>
            <button className="btn btn-primary" onClick={handleSave}>💾 שמור</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── ORDERS SECTION ─────────────────────────────────────────────────────────
function OrdersSection() {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = () => api.get('/orders').then(setOrders);
  useEffect(() => {
    load();
    const interval = setInterval(load, 30000); // auto-refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const updateStatus = async (id, status) => {
    await api.put(`/orders/${id}/status`, { status });
    load();
  };

  const printOrder = (order) => {
    const win = window.open('', '_blank', 'width=700,height=800');
    const items = order.items;
    const rows = items.map((item, i) => `
      <tr>
        <td>${item.product_code || String(item.id).padStart(4, '0')}</td>
        <td>${item.name}</td>
        <td style="text-align:center">${item.quantity}</td>
        <td style="text-align:left">₪${(item.sale_price || item.price).toFixed(2)}</td>
        <td style="text-align:left">₪${((item.sale_price || item.price) * item.quantity).toFixed(2)}</td>
      </tr>`).join('');
    win.document.write(`
      <!DOCTYPE html><html dir="rtl" lang="he">
      <head>
        <meta charset="utf-8">
        <title>הזמנה #${order.id}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 32px; color: #222; direction: rtl; }
          h1 { font-size: 22px; margin-bottom: 4px; }
          .sub { color: #666; font-size: 14px; margin-bottom: 24px; }
          .info-row { display: flex; gap: 40px; margin-bottom: 24px; }
          .info-box { background: #f5f5f5; border-radius: 8px; padding: 12px 18px; }
          .info-label { font-size: 11px; color: #888; margin-bottom: 3px; }
          .info-value { font-size: 15px; font-weight: 700; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
          th { background: #222; color: white; padding: 10px 12px; text-align: right; font-size: 13px; }
          th:nth-child(3), th:nth-child(4), th:nth-child(5) { text-align: center; }
          td { padding: 9px 12px; border-bottom: 1px solid #eee; font-size: 14px; }
          td:nth-child(3) { text-align: center; }
          td:nth-child(4), td:nth-child(5) { text-align: left; }
          tr:last-child td { border-bottom: none; }
          .total-row { text-align: left; font-size: 18px; font-weight: 800; margin-top: 8px; padding-top: 12px; border-top: 2px solid #222; }
          .footer { margin-top: 40px; font-size: 12px; color: #aaa; border-top: 1px solid #eee; padding-top: 12px; }
          @media print { body { padding: 16px; } }
        </style>
      </head>
      <body>
        <h1>הזמנה #${order.id}</h1>
        <div class="sub">${new Date(order.created_at).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
        <div class="info-row">
          <div class="info-box">
            <div class="info-label">שם המזמין</div>
            <div class="info-value">${order.customer_name}</div>
          </div>
          <div class="info-box">
            <div class="info-label">טלפון</div>
            <div class="info-value" dir="ltr">${order.customer_phone}</div>
          </div>
          ${order.customer_email ? `<div class="info-box">
            <div class="info-label">אימייל</div>
            <div class="info-value" dir="ltr">${order.customer_email}</div>
          </div>` : ''}
          <div class="info-box">
            <div class="info-label">נקודת חלוקה</div>
            <div class="info-value">${order.pickup_name}${order.pickup_city ? ` — ${order.pickup_city}` : ''}</div>
          </div>
        </div>
        <table>
          <thead><tr>
            <th>קוד</th><th>שם מוצר</th><th>כמות</th><th>מחיר יחידה</th><th>סה"כ</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="total-row">סה"כ מוצרים: ₪${(order.total - (order.delivery_fee || 0)).toFixed(2)}</div>
        ${order.delivery_fee > 0 ? `<div class="total-row" style="font-size:14px;color:#666;">🚚 דמי משלוח: ₪${parseFloat(order.delivery_fee).toFixed(2)}</div>` : ''}
        <div class="total-row">סה"כ לתשלום: ₪${order.total.toFixed(2)}</div>
        ${order.notes ? `<p style="margin-top:16px;color:#666"><strong>הערות:</strong> ${order.notes}</p>` : ''}
        <div class="footer">הודפס מממשק ניהול • ${new Date().toLocaleDateString('he-IL')}</div>
        <script>window.onload = () => { window.print(); }<\/script>
      </body></html>`);
    win.document.close();
  };

  const deleteOrder = async (id) => {
    if (!window.confirm('למחוק הזמנה זו לצמיתות?')) return;
    await api.delete(`/orders/${id}`);
    load();
  };

  const STATUS_OPTIONS = ['הוזמן', 'נארז', 'נשלח', 'נאסף'];
  const STATUS_COLORS = {
    'הוזמן':  '#5B7FD4',
    'נארז':   '#E67E22',
    'נשלח':   '#3DAB7B',
    'נאסף':   'var(--purple)',
  };

  return (
    <div>
      <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--brown-dark)', marginBottom: 20 }}>📦 הזמנות</h2>
      <table className="admin-table">
        <thead>
          <tr>
            <th>#</th><th>שם</th><th>טלפון</th><th>אימייל</th><th>נקודת חלוקה</th>
            <th>סה"כ</th><th>סטטוס</th><th>תאריך</th><th>פעולות</th>
          </tr>
        </thead>
        <tbody>
          {orders.map(o => (
            <tr key={o.id}>
              <td>#{o.id}</td>
              <td><strong>{o.customer_name}</strong></td>
              <td dir="ltr">{o.customer_phone}</td>
              <td dir="ltr" style={{ fontSize: 13, color: 'var(--text-light)' }}>{o.customer_email || '—'}</td>
              <td>{o.pickup_name} — {o.pickup_city}</td>
              <td><strong>₪{o.total.toFixed(2)}</strong></td>
              <td>
                <select
                  value={o.status || 'הוזמן'}
                  onChange={e => updateStatus(o.id, e.target.value)}
                  style={{
                    padding: '4px 8px', borderRadius: 8, border: 'none',
                    fontWeight: 700, fontSize: 13, cursor: 'pointer',
                    background: STATUS_COLORS[o.status || 'הוזמן'] + '22',
                    color: STATUS_COLORS[o.status || 'הוזמן'],
                  }}>
                  {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </td>
              <td style={{ fontSize: 13 }}>{new Date(o.created_at).toLocaleDateString('he-IL')}</td>
              <td>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-primary btn-sm" onClick={() => setSelected(o)}>👁️</button>
                  <button className="btn btn-primary btn-sm" onClick={() => printOrder(o)}>🖨️</button>
                  <button className="btn btn-danger btn-sm" onClick={() => deleteOrder(o.id)}>🗑</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {selected && (
        <Modal title={`הזמנה #${selected.id} — ${selected.customer_name}`} onClose={() => setSelected(null)}>
          <p style={{ marginBottom: 4 }}><strong>טלפון:</strong> {selected.customer_phone}</p>
          {selected.customer_email && <p style={{ marginBottom: 4 }}><strong>אימייל:</strong> {selected.customer_email}</p>}
          <p style={{ marginBottom: 16 }}><strong>נקודת חלוקה:</strong> {selected.pickup_name}, {selected.pickup_city}</p>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
            <thead><tr style={{ background: 'var(--bg-soft)' }}>
              <th style={{ padding: '8px', textAlign: 'right' }}>מוצר</th>
              <th style={{ padding: '8px', textAlign: 'center' }}>כמות</th>
              <th style={{ padding: '8px', textAlign: 'center' }}>מחיר</th>
            </tr></thead>
            <tbody>
              {selected.items.map((item, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '8px' }}>{item.name}</td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>{item.quantity}</td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>₪{((item.sale_price || item.price) * item.quantity).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ fontSize: 18, fontWeight: 800, color: 'var(--primary-dark)' }}>סה"כ: ₪{selected.total.toFixed(2)}</p>
          {selected.notes && <p style={{ marginTop: 8, color: 'var(--text-light)' }}><strong>הערות:</strong> {selected.notes}</p>}
          <div className="modal-actions">
            <button className="btn btn-cancel" onClick={() => setSelected(null)}>סגור</button>
            <button className="btn btn-primary" onClick={() => printOrder(selected)}>🖨️ הדפס הזמנה</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── FINANCIAL SECTION ───────────────────────────────────────────────────────
function FinancialSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPayModal, setShowPayModal] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [editModal, setEditModal] = useState(null);
  const [editAmount, setEditAmount] = useState('');

  const load = () => {
    setLoading(true);
    api.get('/financial').then(d => { setData(d); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const markPaid = async (id) => {
    await api.put(`/financial/maaser-payment/${id}/pay`, {});
    load();
  };

  const deleteMaaser = async (id) => {
    if (!window.confirm('למחוק רשומה זו?')) return;
    await api.delete(`/financial/maaser-payment/${id}`);
    load();
  };

  const openEdit = (m) => { setEditAmount(m.amount.toFixed(2)); setEditModal(m); };

  const saveEdit = async () => {
    if (!editAmount || isNaN(editAmount)) { alert('נא להכניס סכום תקין'); return; }
    await api.put(`/financial/maaser-payment/${editModal.id}`, { amount: parseFloat(editAmount) });
    setEditModal(null);
    load();
  };

  const addPayment = async () => {
    if (!payAmount || isNaN(payAmount)) { alert('נא להכניס סכום תקין'); return; }
    await api.post('/financial/maaser-payment', { amount: parseFloat(payAmount) });
    setPayAmount('');
    setShowPayModal(false);
    load();
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-light)' }}>טוען נתונים...</div>;

  const statCard = (label, value, color, emoji) => (
    <div style={{ background: 'white', borderRadius: 14, padding: '20px 24px', boxShadow: 'var(--shadow)', border: `2px solid ${color}22` }}>
      <div style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 6 }}>{emoji} {label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color }}>{typeof value === 'number' ? `₪${value.toFixed(2)}` : value}</div>
    </div>
  );

  return (
    <div>
      <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--brown-dark)', marginBottom: 24 }}>💰 ניהול כספי</h2>

      <div style={{ background: 'var(--bg-soft)', borderRadius: 10, padding: '10px 16px', marginBottom: 20, fontSize: 13, color: 'var(--text-light)' }}>
        * הנתונים מחושבים על הזמנות בסטטוס <strong>"נאסף"</strong> בלבד ({data.ordersCount} הזמנות)
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 32 }}>
        {statCard('הכנסות', data.revenue, '#3DAB7B', '📈')}
        {statCard('הוצאות (עלות)', data.cost, '#E05370', '📉')}
        {statCard('רווח נטו', data.profit, '#5B7FD4', '💎')}
        {statCard('סה"כ מעשרות (10%)', data.maaser, '#7C5CBF', '🕍')}
        {statCard('מעשרות ששולמו', data.totalMaaserPaid, '#3DAB7B', '✅')}
        {statCard('מעשרות לתשלום', data.pendingMaaser, data.pendingMaaser > 0 ? '#E67E22' : '#3DAB7B', '⏳')}
      </div>

      {/* מעשרות */}
      <div style={{ background: 'white', borderRadius: 14, padding: 24, boxShadow: 'var(--shadow)', marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 18, fontWeight: 800, color: 'var(--purple)' }}>🕍 היסטוריית מעשרות</h3>
          <button className="btn btn-primary" onClick={() => { setPayAmount(data.pendingMaaser.toFixed(2)); setShowPayModal(true); }}>
            + רשום תשלום לצדקה
          </button>
        </div>

        {data.maaserPayments.length === 0 ? (
          <p style={{ color: 'var(--text-light)', fontSize: 14 }}>אין תשלומים רשומים עדיין.</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>#</th><th>סכום</th><th>תאריך רישום</th><th>סטטוס</th><th>תאריך תשלום</th><th>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {data.maaserPayments.map(m => (
                <tr key={m.id}>
                  <td>{m.id}</td>
                  <td><strong>₪{m.amount.toFixed(2)}</strong></td>
                  <td>{new Date(m.created_at).toLocaleDateString('he-IL')}</td>
                  <td>
                    {m.paid
                      ? <span style={{ color: 'var(--green)', fontWeight: 700 }}>✅ שולם לצדקה</span>
                      : <span style={{ color: '#E67E22', fontWeight: 700 }}>⏳ ממתין לתשלום</span>}
                  </td>
                  <td style={{ fontSize: 13 }}>{m.paid_at ? new Date(m.paid_at).toLocaleDateString('he-IL') : '—'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {!m.paid && (
                        <button className="btn btn-primary btn-sm" onClick={() => markPaid(m.id)}>✅ שולם</button>
                      )}
                      <button className="btn btn-primary btn-sm" onClick={() => openEdit(m)}>✏️</button>
                      <button className="btn btn-danger btn-sm" onClick={() => deleteMaaser(m.id)}>🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showPayModal && (
        <Modal title="רישום תשלום מעשר לצדקה" onClose={() => setShowPayModal(false)}>
          <p style={{ fontSize: 14, color: 'var(--text-light)', marginBottom: 16 }}>
            המעשר המחושב לתשלום: <strong>₪{data.pendingMaaser.toFixed(2)}</strong>
          </p>
          <div className="form-group">
            <label className="form-label">סכום לרישום (₪)</label>
            <input className="form-input" type="number" value={payAmount} onChange={e => setPayAmount(e.target.value)} />
          </div>
          <div className="modal-actions">
            <button className="btn btn-cancel" onClick={() => setShowPayModal(false)}>ביטול</button>
            <button className="btn btn-primary" onClick={addPayment}>💾 שמור</button>
          </div>
        </Modal>
      )}

      {editModal && (
        <Modal title={`✏️ עריכת רשומה #${editModal.id}`} onClose={() => setEditModal(null)}>
          <div className="form-group">
            <label className="form-label">סכום (₪)</label>
            <input className="form-input" type="number" value={editAmount} onChange={e => setEditAmount(e.target.value)} />
          </div>
          <div className="modal-actions">
            <button className="btn btn-cancel" onClick={() => setEditModal(null)}>ביטול</button>
            <button className="btn btn-primary" onClick={saveEdit}>💾 שמור</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── EMAIL LIST WIDGET ────────────────────────────────────────────────────────
function EmailListWidget() {
  const [emails, setEmails] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(false);

  const load = () => {
    setError(false);
    fetch('/api/orders/emails')
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) setEmails(data);
        else setError(true);
      })
      .catch(() => setError(true));
  };

  const copy = () => {
    if (!emails || emails.length === 0) return;
    navigator.clipboard.writeText(emails.join(', ')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <div>
      {!emails && !error && (
        <button className="btn btn-primary" onClick={load}>📋 הצג רשימת מיילים</button>
      )}
      {error && (
        <div>
          <p style={{ color: 'var(--red)', fontSize: 14, marginBottom: 8 }}>שגיאה בטעינה</p>
          <button className="btn btn-primary" onClick={load}>נסה שוב</button>
        </div>
      )}
      {emails && emails.length === 0 && (
        <p style={{ color: 'var(--text-light)', fontSize: 14 }}>אין כתובות מייל בהזמנות עדיין.</p>
      )}
      {emails && emails.length > 0 && (
        <>
          <div style={{ display: 'flex', gap: 10, marginBottom: 12, alignItems: 'center' }}>
            <span style={{ fontSize: 14, color: 'var(--text-light)' }}>{emails.length} כתובות (ללא כפילויות)</span>
            <button className="btn btn-primary btn-sm" onClick={copy}>
              {copied ? '✅ הועתק!' : '📋 העתק הכל'}
            </button>
            <button className="btn btn-cancel btn-sm" onClick={() => setEmails(null)}>רענן</button>
          </div>
          <div style={{
            background: 'var(--bg-soft)', borderRadius: 8, padding: '10px 14px',
            fontSize: 13, direction: 'ltr', lineHeight: 2, maxHeight: 160, overflowY: 'auto',
            border: '1px solid var(--border)', wordBreak: 'break-all'
          }}>
            {emails.join(', ')}
          </div>
        </>
      )}
    </div>
  );
}

// ─── SITE SETTINGS SECTION ───────────────────────────────────────────────────
function ContactSection() {
  const [form, setForm] = useState({
    store_name: '', contact_phone: '', contact_email: '',
    banner_text: '', banner_visible: 'true',
  });
  const [saved, setSaved] = useState(false);
  const [logoPreview, setLogoPreview] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    api.get('/settings').then(s => {
      setForm({
        store_name: s.store_name || '',
        contact_phone: s.contact_phone || '',
        contact_email: s.contact_email || '',
        banner_text: s.banner_text || 'מחירים מיוחדים למוסדות וכמויות',
        banner_visible: s.banner_visible !== 'false' ? 'true' : 'false',
      });
      if (s.store_logo) setLogoPreview(s.store_logo);
    });
  }, []);

  const handleSave = async () => {
    for (const [key, value] of Object.entries(form)) {
      await api.post('/settings', { key, value });
    }
    if (logoFile) {
      setUploadingLogo(true);
      const fd = new FormData();
      fd.append('logo', logoFile);
      await api.postForm('/settings/logo', fd);
      setUploadingLogo(false);
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleLogoFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div>
      <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--primary-dark)', marginBottom: 24 }}>⚙️ הגדרות אתר</h2>

      {/* ─── כללי ─── */}
      <div style={{ background: 'white', borderRadius: 14, padding: 24, boxShadow: 'var(--shadow)', maxWidth: 560, marginBottom: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: 'var(--brown-dark)' }}>🏪 כותרת ולוגו</h3>
        <div className="form-group">
          <label className="form-label">שם החנות</label>
          <input className="form-input" value={form.store_name} onChange={set('store_name')} placeholder="חנות יצירות ומלאכות" />
        </div>
        <div className="form-group">
          <label className="form-label">לוגו</label>
          <input type="file" accept="image/*" onChange={handleLogoFile} style={{ fontSize: 14 }} />
          {logoPreview && (
            <img src={logoPreview} alt="לוגו" style={{ marginTop: 10, height: 60, borderRadius: 8, objectFit: 'contain', border: '1px solid var(--border)' }} />
          )}
        </div>
      </div>

      {/* ─── באנר ─── */}
      <div style={{ background: 'white', borderRadius: 14, padding: 24, boxShadow: 'var(--shadow)', maxWidth: 560, marginBottom: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: 'var(--brown-dark)' }}>📢 הודעת באנר בקטלוג</h3>
        <div className="form-group">
          <label className="form-label">טקסט ההודעה</label>
          <input className="form-input" value={form.banner_text} onChange={set('banner_text')} placeholder="מחירים מיוחדים למוסדות וכמויות" />
        </div>
        <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <input type="checkbox" id="banner_visible" checked={form.banner_visible === 'true'}
            onChange={e => setForm(f => ({ ...f, banner_visible: e.target.checked ? 'true' : 'false' }))} />
          <label htmlFor="banner_visible" style={{ cursor: 'pointer', fontWeight: 600 }}>הצג הודעה באתר</label>
        </div>
      </div>

      {/* ─── יצירת קשר ─── */}
      <div style={{ background: 'white', borderRadius: 14, padding: 24, boxShadow: 'var(--shadow)', maxWidth: 560, marginBottom: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: 'var(--brown-dark)' }}>📞 פרטי יצירת קשר</h3>
        <div className="form-group">
          <label className="form-label">📱 מספר טלפון</label>
          <input className="form-input" value={form.contact_phone} onChange={set('contact_phone')} placeholder="050-1234567" dir="ltr" />
        </div>
        <div className="form-group">
          <label className="form-label">✉️ אימייל</label>
          <input className="form-input" value={form.contact_email} onChange={set('contact_email')} placeholder="contact@example.com" dir="ltr" />
        </div>
      </div>

      {/* ─── רשימת מיילים ─── */}
      <div style={{ background: 'white', borderRadius: 14, padding: 24, boxShadow: 'var(--shadow)', maxWidth: 560, marginBottom: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12, color: 'var(--brown-dark)' }}>📧 כתובות מייל של לקוחות</h3>
        <EmailListWidget />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={uploadingLogo}>
          {uploadingLogo ? 'מעלה...' : '💾 שמור הגדרות'}
        </button>
        {saved && <span style={{ color: 'var(--green)', fontWeight: 700, fontSize: 14 }}>✅ נשמר בהצלחה!</span>}
      </div>
    </div>
  );
}

// ─── ADMIN PAGE ─────────────────────────────────────────────────────────────
export default function AdminPage() {
  const [loggedIn, setLoggedIn] = useState(checkAdminAuth());
  const [tab, setTab] = useState('products');

  const tabs = [
    { id: 'products',    label: '🛍️ מוצרים' },
    { id: 'categories',  label: '📂 קטגוריות' },
    { id: 'pickup',      label: '📍 נקודות חלוקה' },
    { id: 'orders',      label: '📦 הזמנות' },
    { id: 'financial',   label: '💰 ניהול כספי' },
    { id: 'contact',     label: '⚙️ הגדרות אתר' },
  ];

  const handleLogout = () => {
    sessionStorage.removeItem('admin_logged_in');
    sessionStorage.removeItem('admin_token');
    setLoggedIn(false);
  };

  if (!loggedIn) return <AdminLogin onLogin={() => setLoggedIn(true)} />;

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        {tabs.map(t => (
          <button key={t.id} className={`admin-sidebar-btn ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
        <button onClick={handleLogout} style={{ display: 'block', width: '100%', textAlign: 'right', padding: '12px 20px', background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: 14, cursor: 'pointer', marginTop: 'auto', position: 'absolute', bottom: 0 }}>
          🚪 התנתק
        </button>
      </aside>
      <div className="admin-content">
        {tab === 'products'   && <ProductsSection />}
        {tab === 'categories' && <CategoriesSection />}
        {tab === 'pickup'     && <PickupSection />}
        {tab === 'orders'     && <OrdersSection />}
        {tab === 'financial'  && <FinancialSection />}
        {tab === 'contact'    && <ContactSection />}
      </div>
    </div>
  );
}
